import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { Save, Cancel, ArrowBack } from '@mui/icons-material';

import customerService from '../../services/customerService';
import { usersApi } from '../../services/api';
import { 
  CreateCustomerDto, 
  UpdateCustomerDto, 
  Customer, 
  CustomerStatus,
  CUSTOMER_STATUS_OPTIONS 
} from '../../types/customer';
import { User } from '../../types';

type CustomerFormData = {
  user_id: number;
  company_name: string;
  contact_person: string;
  phone: string;
  alternate_phone: string;
  status: CustomerStatus;
};

const validationSchema = yup.object({
  user_id: yup
    .number()
    .required('User is required')
    .positive('User must be selected'),
  company_name: yup
    .string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters'),
  contact_person: yup
    .string()
    .required('Contact person is required')
    .min(2, 'Contact person must be at least 2 characters'),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[+]?[\d\s\-()]+$/, 'Please enter a valid phone number'),
  alternate_phone: yup
    .string()
    .default('')
    .matches(/^[+]?[\d\s\-()]*$/, 'Please enter a valid phone number'),
  status: yup
    .string()
    .oneOf(['active', 'inactive', 'suspended'] as CustomerStatus[])
    .required('Status is required')
});

const CustomerForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch
  } = useForm<CustomerFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      user_id: 0,
      company_name: '',
      contact_person: '',
      phone: '',
      alternate_phone: '',
      status: 'active'
    }
  });

  // Load users for selection
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await usersApi.getAll();
        setUsers(usersData);
      } catch (error) {
        console.error('Error loading users:', error);
        enqueueSnackbar(t('customers.messages.loadUsersError'), { variant: 'error' });
      }
    };

    loadUsers();
  }, [t, enqueueSnackbar]);

  // Load customer data for editing
  useEffect(() => {
    if (!isEdit || !id) return;

    const loadCustomer = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        
        const customerData = await customerService.getCustomer(parseInt(id));
        setCustomer(customerData);
        
        // Populate form with customer data
        reset({
          user_id: customerData.user_id,
          company_name: customerData.company_name,
          contact_person: customerData.contact_person,
          phone: customerData.phone,
          alternate_phone: customerData.alternate_phone || '',
          status: customerData.status
        });
      } catch (error: any) {
        console.error('Error loading customer:', error);
        const errorMessage = error.response?.data?.message || error.message || t('customers.messages.loadError');
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };

    loadCustomer();
  }, [id, isEdit, t, enqueueSnackbar, reset]);

  const onSubmit: SubmitHandler<CustomerFormData> = async (data) => {
    try {
      setLoading(true);
      setError(null);

      if (isEdit && customer) {
        // Update existing customer
        const updateData: UpdateCustomerDto = {
          company_name: data.company_name,
          contact_person: data.contact_person,
          phone: data.phone,
          alternate_phone: data.alternate_phone || undefined,
          status: data.status
        };

        await customerService.updateCustomer(customer.user_id, updateData);
        enqueueSnackbar(t('customers.messages.updated'), { variant: 'success' });
      } else {
        // Create new customer
        const createData: CreateCustomerDto = {
          user_id: data.user_id,
          company_name: data.company_name,
          contact_person: data.contact_person,
          phone: data.phone,
          alternate_phone: data.alternate_phone || undefined,
          status: data.status
        };

        await customerService.createCustomer(createData);
        enqueueSnackbar(t('customers.messages.created'), { variant: 'success' });
      }

      navigate('/customers');
    } catch (error: any) {
      console.error('Error saving customer:', error);
      const errorMessage = error.response?.data?.message || error.message || 
        (isEdit ? t('customers.messages.updateError') : t('customers.messages.createError'));
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  if (initialLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/customers')}
          sx={{ mr: 2 }}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1">
          {isEdit ? t('customers.editCustomer') : t('customers.createCustomer')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* User Selection - Only for create */}
              {!isEdit && (
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.user_id}>
                    <InputLabel>{t('customers.fields.user')}</InputLabel>
                    <Select
                      {...register('user_id')}
                      value={watch('user_id') || ''}
                      onChange={(e) => setValue('user_id', Number(e.target.value))}
                      label={t('customers.fields.user')}
                    >
                      <MenuItem value="">
                        <em>{t('common.selectOption')}</em>
                      </MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.user_id && (
                      <Typography variant="caption" color="error">
                        {errors.user_id.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* Company Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('customers.sections.companyInfo')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('company_name')}
                  label={t('customers.fields.companyName')}
                  fullWidth
                  error={!!errors.company_name}
                  helperText={errors.company_name?.message}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('contact_person')}
                  label={t('customers.fields.contactPerson')}
                  fullWidth
                  error={!!errors.contact_person}
                  helperText={errors.contact_person?.message}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('phone')}
                  label={t('customers.fields.phone')}
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('alternate_phone')}
                  label={t('customers.fields.alternatePhone')}
                  fullWidth
                  error={!!errors.alternate_phone}
                  helperText={errors.alternate_phone?.message}
                />
              </Grid>

              {/* Status - Only for edit */}
              {isEdit && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>{t('customers.fields.status')}</InputLabel>
                    <Select
                      {...register('status')}
                      value={watch('status') || 'active'}
                      onChange={(e) => setValue('status', e.target.value as CustomerStatus)}
                      label={t('customers.fields.status')}
                    >
                      {CUSTOMER_STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(`customers.status.${option.value}`)}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.status && (
                      <Typography variant="caption" color="error">
                        {errors.status.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* Form Actions */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting || loading}
                    startIcon={<Cancel />}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  >
                    {isEdit ? t('common.update') : t('common.create')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CustomerForm;
